import React, { useState, useEffect, useRef } from 'react'
import { supabase } from '../lib/supabase'
import { getTimeMultiplier } from '../lib/scores'

const DAYS = ['M','T','W','T','F','S','S']
const DAY_KEYS = ['mon','tue','wed','thu','fri','sat','sun']

const AREA_COLORS = {
  health: '#4caf7d', mind: '#9b5ce0', career: '#c9a84c',
  relationships: '#e05c5c', faith: '#5c8fe0', purpose: '#e07d3c',
}
const AREAS = [
  { key: 'health', label: 'Health' }, { key: 'mind', label: 'Mind' },
  { key: 'career', label: 'Career' }, { key: 'relationships', label: 'Relationships' },
  { key: 'faith', label: 'Faith' }, { key: 'purpose', label: 'Goals' },
]

const WORKOUT_KEYWORDS = ['workout','exercise','weightlift','weights','gym','lift','training','run','cardio','crossfit','jog','swim','cycling','bike','hiit','strength']

function getLocalDate() {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`
}

function getTodayKey() {
  return DAY_KEYS[new Date().getDay() === 0 ? 6 : new Date().getDay() - 1]
}

function isWorkoutHabit(title) {
  const lower = title.toLowerCase()
  return WORKOUT_KEYWORDS.some(k => lower.includes(k))
}

function applyCurve(raw) {
  if (raw <= 0) return 0
  if (raw >= 100) return 100
  return Math.min(100, Math.round(Math.pow(raw / 100, 0.65) * 100))
}

export default function HabitsTab({ userId, onPointsUpdated, showAdd, onAddClose }) {
  const [habits, setHabits] = useState([])
  const [completions, setCompletions] = useState({})
  const [loading, setLoading] = useState(true)

  // Add/Edit overlay
  const [editHabit, setEditHabit] = useState(null) // null = closed, {} = new, {id,...} = editing
  const [habitTitle, setHabitTitle] = useState('')
  const [habitDays, setHabitDays] = useState([0,1,2,3,4,5,6])
  const [habitArea, setHabitArea] = useState('health')
  const [habitSaving, setHabitSaving] = useState(false)

  // Complete habit overlay
  const [completingHabit, setCompletingHabit] = useState(null)
  const [completeDesc, setCompleteDesc] = useState('')
  const [completeTime, setCompleteTime] = useState('')
  const [completing, setCompleting] = useState(false)

  // Long press
  const pressTimer = useRef(null)

  useEffect(() => {
    if (userId) { loadHabits(); loadCompletions() }
  }, [userId])

  // Open add from parent
  useEffect(() => {
    if (showAdd) openAdd()
  }, [showAdd])

  async function loadHabits() {
    const { data } = await supabase
      .from('user_habits')
      .select('*')
      .eq('user_id', userId)
      .order('created_at')
    setHabits(data || [])
    setLoading(false)
  }

  async function loadCompletions() {
    const { data } = await supabase
      .from('habit_completions')
      .select('habit_id')
      .eq('user_id', userId)
      .eq('date', getLocalDate())
    const map = {}
    data?.forEach(c => { map[c.habit_id] = true })
    setCompletions(map)
  }

  function openAdd() {
    setHabitTitle('')
    setHabitDays([0,1,2,3,4,5,6])
    setHabitArea('health')
    setEditHabit({})
  }

  function openEdit(habit) {
    setHabitTitle(habit.title)
    setHabitDays(habit.days || [0,1,2,3,4,5,6])
    setHabitArea(habit.area || 'health')
    setEditHabit(habit)
  }

  function toggleDay(i) {
    setHabitDays(prev =>
      prev.includes(i) ? prev.filter(d => d !== i) : [...prev, i]
    )
  }

  async function saveHabit() {
    if (!habitTitle.trim()) return
    setHabitSaving(true)
    if (editHabit?.id) {
      const { data } = await supabase.from('user_habits')
        .update({ title: habitTitle.trim(), days: habitDays, area: habitArea })
        .eq('id', editHabit.id).select().single()
      if (data) setHabits(prev => prev.map(h => h.id === data.id ? data : h))
    } else {
      const { data } = await supabase.from('user_habits')
        .insert({ user_id: userId, title: habitTitle.trim(), days: habitDays, area: habitArea })
        .select().single()
      if (data) setHabits(prev => [...prev, data])
    }
    setHabitSaving(false)
    setEditHabit(null)
    onAddClose?.()
  }

  async function deleteHabit() {
    if (!editHabit?.id) return
    await supabase.from('user_habits').delete().eq('id', editHabit.id)
    setHabits(prev => prev.filter(h => h.id !== editHabit.id))
    setEditHabit(null)
  }

  function startLongPress(habit) {
    pressTimer.current = setTimeout(() => openEdit(habit), 600)
  }

  function cancelLongPress() {
    clearTimeout(pressTimer.current)
  }

  function handleCircleTap(habit) {
    if (completions[habit.id]) return // already done
    setCompletingHabit(habit)
    setCompleteDesc('')
    setCompleteTime(30)
  }

  async function completeHabit() {
    if (!completeDesc.trim() || !completingHabit) return
    setCompleting(true)

    // Evaluate with Claude
    let basePoints = 20
    let area = completingHabit.area || 'health'
    let category = completingHabit.title
    let reasoning = ''
    try {
      const res = await fetch('/api/evaluate-task', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ description: completeDesc.trim(), timeSpent: completeTime, userId })
      })
      const data = await res.json()
      if (!data.error && !data.flagged) {
        basePoints = data.base_points || basePoints
        category = data.category || category
        reasoning = data.reasoning || ''
      }
    } catch {}

    const finalPoints = basePoints

    // Save to manual_tasks
    await supabase.from('manual_tasks').insert({
      user_id: userId,
      date: getLocalDate(),
      description: completeDesc.trim(),
      category,
      area,
      base_points: basePoints,
      time_minutes: completeTime,
      final_points: finalPoints,
      flagged: false,
      flag_reason: '',
    })

    // Mark habit complete
    await supabase.from('habit_completions').upsert({
      user_id: userId,
      habit_id: completingHabit.id,
      date: getLocalDate(),
    }, { onConflict: 'user_id,habit_id,date' })

    setCompletions(prev => ({ ...prev, [completingHabit.id]: true }))
    setCompletingHabit(null)
    setCompleting(false)
    onPointsUpdated?.()
  }

  // Only show habits scheduled for today
  const todayIndex = new Date().getDay() === 0 ? 6 : new Date().getDay() - 1
  const todayHabits = habits.filter(h => (h.days || [0,1,2,3,4,5,6]).includes(todayIndex))

  if (loading) return <div style={s.empty}>Loading...</div>

  return (
    <>
      {todayHabits.length === 0 ? (
        <div style={s.emptyWrap}>
          <div style={s.emptyIcon}>🔄</div>
          <div style={s.emptyTitle}>No Habits Today</div>
          <div style={s.emptyText}>Tap "Add Habit" from the + menu to build your daily habits.</div>
        </div>
      ) : (
        <div style={s.list}>
          {todayHabits.map(habit => {
            const done = completions[habit.id]
            const color = AREA_COLORS[habit.area] || '#c9a84c'
            const dayLabel = habit.days?.length === 7 ? 'every day' :
              habit.days?.map(d => DAYS[d]).join(', ') || 'every day'
            return (
              <div key={habit.id} style={s.habitRow}
                onMouseDown={() => startLongPress(habit)}
                onMouseUp={cancelLongPress}
                onMouseLeave={cancelLongPress}
                onTouchStart={() => startLongPress(habit)}
                onTouchEnd={cancelLongPress}
                onTouchMove={cancelLongPress}>
                {/* Color indicator */}
                <div style={{ ...s.colorBar, background: color }} />
                <div style={s.habitInfo}>
                  <div style={s.habitTitle}>{habit.title}</div>
                  <div style={s.habitSub}>{dayLabel}</div>
                </div>
                {/* Check circle */}
                <div style={{ ...s.circle, borderColor: done ? color : 'rgba(255,255,255,0.2)', background: done ? color : 'transparent' }}
                  onClick={() => handleCircleTap(habit)}>
                  {done && <span style={s.checkMark}>✓</span>}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* ADD / EDIT OVERLAY */}
      {editHabit !== null && (
        <div style={s.overlay} onClick={() => { setEditHabit(null); onAddClose?.() }}>
          <div style={s.box} onClick={e => e.stopPropagation()}>
            <div style={s.boxHeader}>
              <div style={s.boxClose} onClick={() => { setEditHabit(null); onAddClose?.() }}>✕</div>
              <div style={s.boxTitle}>{editHabit?.id ? 'Edit Habit' : 'Add Habit'}</div>
              <div style={{ ...s.boxSave, opacity: habitTitle.trim() ? 1 : 0.35 }} onClick={saveHabit}>
                {habitSaving ? '...' : 'Save'}
              </div>
            </div>

            <div style={s.fieldLabel}>TITLE</div>
            <input style={s.input} value={habitTitle}
              onChange={e => setHabitTitle(e.target.value)}
              placeholder="e.g. Morning Run" autoFocus />

            <div style={s.fieldLabel}>FREQUENCY</div>
            <div style={s.dayRow}>
              {DAYS.map((d, i) => (
                <div key={i}
                  style={{ ...s.dayCircle, background: habitDays.includes(i) ? '#4caf7d' : 'rgba(255,255,255,0.08)', color: habitDays.includes(i) ? '#000' : '#6b6860', fontWeight: habitDays.includes(i) ? '700' : '400' }}
                  onClick={() => toggleDay(i)}>{d}</div>
              ))}
            </div>

            <div style={s.fieldLabel}>LIFE AREA</div>
            <div style={s.areaGrid}>
              {AREAS.map(a => (
                <div key={a.key}
                  style={{ ...s.areaChip, background: habitArea === a.key ? `${AREA_COLORS[a.key]}22` : 'rgba(255,255,255,0.05)', border: habitArea === a.key ? `1px solid ${AREA_COLORS[a.key]}` : '1px solid rgba(255,255,255,0.08)', color: habitArea === a.key ? AREA_COLORS[a.key] : '#6b6860', fontWeight: habitArea === a.key ? '600' : '400' }}
                  onClick={() => setHabitArea(a.key)}>{a.label}</div>
              ))}
            </div>

            {editHabit?.id && (
              <div style={s.deleteBtn} onClick={deleteHabit}>Delete Habit</div>
            )}
          </div>
        </div>
      )}

      {/* COMPLETE HABIT OVERLAY */}
      {completingHabit && (
        <div style={s.overlay} onClick={() => setCompletingHabit(null)}>
          <div style={s.box} onClick={e => e.stopPropagation()}>
            <div style={s.boxHeader}>
              <div style={s.boxClose} onClick={() => setCompletingHabit(null)}>✕</div>
              <div style={s.boxTitle}>Log Habit</div>
              <div style={{ width: 36 }} />
            </div>
            <div style={s.habitNameLabel}>{completingHabit.title}</div>

            <div style={s.fieldLabel}>WHAT DID YOU DO?</div>
            <textarea style={s.textarea}
              placeholder="Describe what you did in detail..."
              value={completeDesc}
              onChange={e => setCompleteDesc(e.target.value)}
              rows={3} autoFocus />

            <div style={s.fieldLabel}>HOW LONG DID THIS TAKE?</div>
            <input style={s.input}
              placeholder="e.g. 45 minutes, 1 hour..."
              value={completeTime}
              onChange={e => setCompleteTime(e.target.value)} />

            <div style={{ ...s.completeBtn, opacity: completing || !completeDesc.trim() ? 0.5 : 1 }}
              onClick={completeHabit}>
              {completing ? 'Saving...' : 'Complete & Earn Points →'}
            </div>
          </div>
        </div>
      )}
    </>
  )
}

const s = {
  list: { display: 'flex', flexDirection: 'column', gap: '10px' },
  habitRow: { display: 'flex', alignItems: 'center', gap: '14px', padding: '16px', background: '#1a1a26', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '14px', userSelect: 'none', cursor: 'default' },
  colorBar: { width: '4px', height: '36px', borderRadius: '2px', flexShrink: 0 },
  habitInfo: { flex: 1 },
  habitTitle: { fontSize: '15px', fontWeight: '600', color: '#e8e6df', marginBottom: '3px' },
  habitSub: { fontSize: '12px', color: '#6b6860' },
  circle: { width: '32px', height: '32px', borderRadius: '50%', border: '2px solid', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0, transition: 'all 0.2s' },
  checkMark: { fontSize: '14px', color: '#000', fontWeight: '700' },
  emptyWrap: { display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '60px 24px', textAlign: 'center', gap: '12px' },
  emptyIcon: { fontSize: '48px' },
  emptyTitle: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '24px', letterSpacing: '2px', color: '#e8e6df' },
  emptyText: { fontSize: '14px', color: '#6b6860', lineHeight: '1.6', maxWidth: '260px' },
  empty: { textAlign: 'center', color: '#6b6860', padding: '40px 0' },
  // Overlay
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', backdropFilter: 'blur(8px)', zIndex: 400, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' },
  box: { background: '#1a1a26', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '20px', padding: '24px', width: '100%', maxWidth: '400px', display: 'flex', flexDirection: 'column', gap: '12px' },
  boxHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' },
  boxClose: { width: '32px', height: '32px', borderRadius: '50%', background: 'rgba(255,255,255,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', color: '#6b6860', cursor: 'pointer' },
  boxTitle: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '22px', letterSpacing: '2px', color: '#e8e6df' },
  boxSave: { background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.3)', color: '#c9a84c', fontSize: '13px', fontWeight: '600', padding: '7px 16px', borderRadius: '20px', cursor: 'pointer' },
  fieldLabel: { fontSize: '11px', letterSpacing: '2px', textTransform: 'uppercase', color: '#6b6860', fontWeight: '600' },
  input: { width: '100%', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', padding: '12px 14px', color: '#e8e6df', fontSize: '15px', fontFamily: "'DM Sans', sans-serif", outline: 'none', boxSizing: 'border-box' },
  dayRow: { display: 'flex', gap: '6px', justifyContent: 'space-between' },
  dayCircle: { width: '38px', height: '38px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '13px', cursor: 'pointer', transition: 'all 0.15s', flexShrink: 0 },
  areaGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' },
  areaChip: { padding: '10px 8px', borderRadius: '10px', fontSize: '12px', textAlign: 'center', cursor: 'pointer' },
  deleteBtn: { marginTop: '4px', padding: '12px', background: 'rgba(224,92,92,0.08)', border: '1px solid rgba(224,92,92,0.2)', borderRadius: '10px', color: '#e05c5c', fontSize: '13px', textAlign: 'center', cursor: 'pointer' },
  // Complete overlay
  habitNameLabel: { fontFamily: "'Bebas Neue', sans-serif", fontSize: '20px', color: '#c9a84c', letterSpacing: '1px', marginTop: '-4px' },
  textarea: { width: '100%', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '10px', padding: '12px 14px', color: '#e8e6df', fontSize: '14px', fontFamily: "'DM Sans', sans-serif", outline: 'none', resize: 'none', lineHeight: '1.5', boxSizing: 'border-box' },
  timeRow: { display: 'flex', gap: '6px' },
  timeChip: { flex: 1, padding: '9px 4px', borderRadius: '8px', fontSize: '12px', textAlign: 'center', cursor: 'pointer' },
  completeBtn: { padding: '14px', background: 'linear-gradient(135deg, #c9a84c, #8b6a1a)', color: '#000', borderRadius: '12px', textAlign: 'center', cursor: 'pointer', fontSize: '14px', fontWeight: '700', marginTop: '4px' },
}
